// Wire protocol. JSON over WebSocket with compact tuples for the high-frequency parts.

import { Derived, HeroSave, SlotId } from './types';

export const PROTOCOL_VERSION = 2;
/** A room closing a socket because its session was revoked. The client signs in again, and doesn't reconnect. */
export const SIGNED_OUT_CLOSE = 4006;

export type C2S =
  /** Who's joining isn't here: the session cookie went with the WebSocket upgrade. */
  | { t: 'join'; hero: string; v: number; /** The browser's analytics session, so that the room's events land in it. */ sid?: string }
  /** One fixed-step movement input. a: direction 0-255, or -1 when standing still. */
  | { t: 'i'; s: number; a: number }
  /** Cast a skill at a world position, optionally aimed at a unit. */
  | { t: 'c'; k: string; x: number; y: number; id?: number }
  /** Interact with a nearby object / item / downed ally. */
  | { t: 'act'; id: number }
  | { t: 'pot'; k: 'hp' | 'mp' }
  | { t: 'equip'; uid: string }
  | { t: 'unequip'; slot: SlotId }
  | { t: 'drop'; uid: string }
  | { t: 'sell'; uid: string }
  | { t: 'buy'; k: 'hp' | 'mp'; n: number }
  | { t: 'stat'; k: 'str' | 'dex' | 'vit' | 'ene' }
  | { t: 'skill'; k: string }
  | { t: 'bind'; i: number; k: string | null }
  | { t: 'respec' }
  | { t: 'stash'; uid: string; to: 'bag' | 'stash' }
  /** Travel through a waygate to a zone, or to an ally by pid. */
  | { t: 'warp'; z?: string; ally?: number }
  | { t: 'arena'; team: 'ffa' | 'red' | 'blue' }
  | { t: 'respawn' }
  | { t: 'chat'; m: string }
  | { t: 'ping'; c: number };

/** Unit state tuple: [id, x*32, y*32, hp%, dirAnim, statusBits]. */
export type UnitRec = [number, number, number, number, number, number];

/** New-entity metadata tuples; second element discriminates. */
export type UnitMeta = [id: number, kind: 0, unitKind: 'player' | 'monster' | 'summon', def: string, name: string, level: number, tier: number, team: number, extra: string];
export type ItemMeta = [id: number, kind: 1, x: number, y: number, rarity: number, icon: string, name: string];
export type ObjMeta = [id: number, kind: 2, type: string, x: number, y: number, state: number, sub: string, to: string];
export type EntityMeta = UnitMeta | ItemMeta | ObjMeta;

/** Fast-changing private player state. */
export type MeRec = [hp: number, mp: number, maxHp: number, maxMp: number, speed: number, xp: number, gold: number, hpPots: number, mpPots: number, stateBits: number];

export const ME_FORCED = 1; // being moved by the server: dash, leap, knockback
export const ME_DOWNED = 2;
export const ME_STUNNED = 4;
export const ME_WHIRL = 8;

/** Animation states packed into dirAnim. dir in low 2 bits: 0 down, 1 up, 2 left, 3 right. */
export const ANIM_IDLE = 0;
export const ANIM_WALK = 1;
export const ANIM_SWING = 2;
export const ANIM_CAST = 3;
export const ANIM_SHOOT = 4;
export const ANIM_SLAM = 5;
export const ANIM_DEAD = 6;
export const ANIM_WHIRL = 7;

export interface PartyEntry {
  pid: number;
  name: string;
  cls: string;
  level: number;
  zone: string;
  hp: number; // percent
  mp: number; // percent
  dead: boolean;
  team: number;
  /** Arena kills. */
  score: number;
}

export type S2C =
  | { t: 'welcome'; pid: number; uid: number; room: { code: string; name: string; seed: number; diff: number } }
  | { t: 'err'; code: string; m: string }
  /** Enter a zone. The client regenerates the map from (seed, id) and verifies `hash`. */
  | { t: 'zone'; id: string; x: number; y: number; hash: number; uid: number }
  | { t: 'hero'; hero: HeroSave; d: Derived }
  | { t: 's'; k: number; a: number; x: number; y: number; m: MeRec; e: UnitRec[]; n: EntityMeta[]; g: number[]; o?: [number, number][]; fx?: Fx[] }
  | { t: 'party'; l: PartyEntry[] }
  | { t: 'chat'; f: string; m: string; c: string }
  | { t: 'pong'; c: number; k: number }
  | { t: 'kick'; m: string }
  /** The room closes for an update in `left` seconds. Sent again with each reminder. */
  | { t: 'closing'; left: number }
  /** The room has closed for an update and everyone's progress is saved. */
  | { t: 'closed' }
  /** Who is in the room's voice chat. `on` is false if voice isn't available on this server. */
  | { t: 'voice'; on: boolean; l: VoiceEntry[] };

/** Someone in the room's voice chat. */
export interface VoiceEntry {
  pid: number;
  /** Stable for this player across heroes and rooms, so that muting them lasts. Reveals nothing else. */
  vid: string;
  /** Set while their mic is live. It changes each time they publish again. Absent if they only listen. */
  gen?: number;
}

/**
 * Voice signaling, sent to POST /api/rooms/:code/voice rather than over the game socket, which has a
 * 4096-character limit that these SDP blobs outgrow. `attempt` identifies one browser connection, so
 * that a late request from a connection the client has replaced can't affect the new one.
 */
export type VoiceRequest =
  /** `at` is when this connection started, for telling which of one player's connections is newest. */
  | { op: 'join'; hero: string; attempt: string; at: number }
  /** Sets up the connection, before any audio. The reply's `sdp` is an offer, answered the same way as a pull's. */
  | { op: 'connect'; hero: string; attempt: string }
  | { op: 'publish'; hero: string; attempt: string; mid: string; sdp: string }
  /** The browser is connected and sending, so others can now be told about its mic. */
  | { op: 'ready'; hero: string; attempt: string }
  | { op: 'pull'; hero: string; attempt: string; pids: number[] }
  | { op: 'answer'; hero: string; attempt: string; sdp: string }
  /** Stops listening to `pids` and, with `mic`, stops publishing. `sdp` is the browser's new offer. */
  | { op: 'close'; hero: string; attempt: string; pids: number[]; mic: boolean; sdp: string }
  | { op: 'leave'; hero: string; attempt: string };

/** One requested stream. `mid` is absent if it couldn't be delivered yet, so the client should retry. */
export interface VoicePull {
  pid: number;
  mid?: string;
  gen?: number;
}

export type VoiceErrorCode =
  /** Voice isn't available on this server or in this room. */
  | 'off'
  /** This hero isn't connected to the room. */
  | 'gone'
  /** This connection has been replaced or closed. Start a new one. */
  | 'stale'
  /** Too many requests. Wait a little and carry on. */
  | 'slow'
  /** Realtime refused or failed the request. Start a new connection. */
  | 'failed';

export type VoiceReply = { ok: true; sdp?: string; tracks?: VoicePull[] } | { ok: false; code: VoiceErrorCode };

/** Off opens no connection at all. Listen never touches the mic. Talk holds it, silent until the key is down. */
export type VoiceMode = 'off' | 'listen' | 'talk';

/** What a player has chosen about voice, from GET /api/voice/settings. It's kept with the player, not the browser. */
export interface VoiceSettings {
  mode: VoiceMode;
  /** The `vid`s of the players they've muted. */
  muted: string[];
}

/** One change, sent to POST /api/voice/settings. One at a time, so that two tabs can't undo each other's choices. */
export type VoiceSettingsChange = { mode: VoiceMode } | { vid: string; muted: boolean };

/**
 * Effect events, all starting [code, x, y, ...] so that the server can filter by distance:
 *  pr: projectile spawn   [pr, x, y, id, vis, vx, vy, life, r, lob(0|1), tx, ty]
 *  pu: projectile update  [pu, x, y, id, vx, vy]
 *  pe: projectile end     [pe, x, y, id]
 *  d:  damage number      [d, x, y, amt, el, crit, targetId]
 *  h:  heal number        [h, x, y, amt, targetId]
 *  v:  visual burst       [v, x, y, vis, r, ang]
 *  t:  telegraph ring     [t, x, y, r, dur, el]
 *  g:  ground effect      [g, x, y, id, vis, r, dur]
 *  ge: ground effect end  [ge, x, y, id]
 *  l:  line effect        [l, x, y, vis, pts[]]
 *  c:  cast animation     [c, x, y, unitId, sfx, tx, ty]
 *  lv: level up           [lv, x, y, unitId]
 *  dth: death             [dth, x, y, unitId]
 *  cd: cooldown (private) [cd, 0, 0, skillId, seconds]
 *  bnr: banner text       [bnr, 0, 0, text, color]
 *  hop: leap              [hop, x, y, unitId, seconds]
 *  ms: floating text      [ms, x, y, text, color]
 *  s:  sound              [s, x, y, sfx]
 */
export type Fx = (string | number | number[])[];

export const ELEMENT_INDEX: Record<string, number> = { phys: 0, fire: 1, cold: 2, light: 3, poison: 4, magic: 5, heal: 6 };
export const ELEMENT_COLORS = ['#fcfcfc', '#f87858', '#3cbcfc', '#f8d878', '#58d854', '#b890fc', '#58f898'];

export interface RoomSummary {
  code: string;
  name: string;
  diff: number;
  players: number;
  max: number;
  avgLevel: number;
  updated: number;
}
